import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { HeroFont } from '../ui/HeroFont';

interface FAQ {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'null' && s !== 'undefined';
}

// ============================================================================
// Accordion item
// ============================================================================

interface FAQItemProps {
  faq: FAQ;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ faq, index, isOpen, onToggle }: FAQItemProps) {
  const questionId = `faq-question-${index}`;
  const panelId = `faq-panel-${index}`;

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <h3 className="faq-item-heading">
        <button
          type="button"
          className="faq-item-trigger"
          id={questionId}
          aria-controls={panelId}
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <span className="faq-item-question">{faq.question || 'Question'}</span>
          <span className="faq-item-icon" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={questionId}
        className="faq-item-panel"
        hidden={!isOpen}
      >
        {hasValue(faq.answer) ? (
          <div
            className="faq-item-answer"
            dangerouslySetInnerHTML={{ __html: faq.answer! }}
          />
        ) : (
          <p className="faq-item-answer faq-item-answer--empty">No answer yet.</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FAQ category group
// ============================================================================

interface FAQGroupProps {
  heading: string | null;
  items: FAQ[];
  baseIndex: number;
  openIndex: number | null;
  onToggle: (index: number) => void;
}

function FAQGroup({ heading, items, baseIndex, openIndex, onToggle }: FAQGroupProps) {
  const groupRef = useScrollReveal<HTMLDivElement>();

  return (
    <div ref={groupRef} className="reveal-fade-up faq-group">
      {heading && <h2 className="faq-group-heading">{heading}</h2>}
      <div className="faq-group-list">
        {items.map((faq, i) => {
          const idx = baseIndex + i;
          return (
            <FAQItem
              key={idx}
              faq={faq}
              index={idx}
              isOpen={openIndex === idx}
              onToggle={() => onToggle(idx)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FAQList() {
  const faqs = useCollectionBinding<FAQ>('faqlist.items', {
    collection: 'faqs',
  });

  const emptyMessage = useContentToken('faqlist.empty_message');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  // Group FAQs by category, preserving original order.
  const groups = useMemo(() => {
    const seen = new Map<string, FAQ[]>();
    const order: string[] = [];
    const items = faqs || [];

    for (const faq of items) {
      const key = hasValue(faq.category) ? faq.category!.trim() : '';
      if (!seen.has(key)) {
        seen.set(key, []);
        order.push(key);
      }
      seen.get(key)!.push(faq);
    }

    // If everything is in the empty-category bucket, don't show any headings.
    const onlyUncategorised = order.length === 1 && order[0] === '';

    let cursor = 0;
    return order.map((key) => {
      const groupItems = seen.get(key)!;
      const heading = onlyUncategorised || key === '' ? null : key;
      const entry = { heading, items: groupItems, baseIndex: cursor };
      cursor += groupItems.length;
      return entry;
    });
  }, [faqs]);

  const hasFaqs = (faqs || []).length > 0;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]"
    >
      <HeroFont />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasFaqs ? (
          <div className="faq-container">
            {groups.map((group, i) => (
              <FAQGroup
                key={`${group.heading ?? 'default'}-${i}`}
                heading={group.heading}
                items={group.items}
                baseIndex={group.baseIndex}
                openIndex={openIndex}
                onToggle={handleToggle}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <EditableToken
              path="faqlist.empty_message"
              as="p"
              className="font-body text-lg"
            >
              {emptyMessage || 'Frequently asked questions will appear here soon.'}
            </EditableToken>
          </div>
        )}

        {/* Schema-lint anchor for the empty-message token when FAQs are present */}
        {hasFaqs && (
          <EditableToken
            path="faqlist.empty_message"
            as="span"
            aria-hidden="true"
            style={{ display: 'none' }}
          >
            {''}
          </EditableToken>
        )}
      </div>

      <style>{`
        .faq-container {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-group + .faq-group {
          padding-top: 0.5rem;
        }

        .faq-group-heading {
          font-family: 'Nunito Sans', sans-serif;
          font-size: 1.5rem;
          font-weight: 400;
          color: var(--vibe-color-text);
          margin: 0 0 1rem;
          letter-spacing: 0.01em;
          border-bottom: 1px solid var(--vibe-color-border);
          padding-bottom: 0.5rem;
        }

        .faq-group-list {
          display: flex;
          flex-direction: column;
        }

        .faq-item {
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .faq-item:first-child {
          border-top: 1px solid var(--vibe-color-border);
        }

        .faq-item-heading {
          margin: 0;
          font-size: 1rem;
        }

        .faq-item-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.1rem 0.25rem;
          background: transparent;
          border: 0;
          cursor: pointer;
          text-align: left;
          font-family: 'Nunito Sans', sans-serif;
          font-size: 1.05rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          transition: color 0.2s ease;
        }
        .faq-item-trigger:hover,
        .faq-item-trigger:focus-visible {
          color: var(--vibe-color-primary);
          outline: none;
        }

        .faq-item-question {
          flex: 1;
          line-height: 1.4;
        }

        .faq-item-icon {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 1px solid var(--vibe-color-border);
          color: var(--vibe-color-text-muted);
          transition: transform 0.3s ease, border-color 0.2s ease, color 0.2s ease;
        }
        .faq-item-trigger:hover .faq-item-icon,
        .faq-item-trigger:focus-visible .faq-item-icon {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }
        .faq-item--open .faq-item-icon {
          transform: rotate(-180deg);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }

        .faq-item-panel {
          padding: 0 0.25rem 1.25rem;
        }

        .faq-item-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
        }
        .faq-item-answer p {
          margin: 0 0 0.75rem;
        }
        .faq-item-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-item-answer ul,
        .faq-item-answer ol {
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-item-answer li {
          margin-bottom: 0.35rem;
        }
        .faq-item-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .faq-item-answer--empty {
          opacity: 0.65;
          font-style: italic;
        }

        @media (prefers-reduced-motion: reduce) {
          .faq-item-icon {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.empty_message': {
      label: 'Empty State Message',
      type: 'text',
      description: 'Shown on the page when no FAQ items have been added yet.',
      defaultValue: 'Frequently asked questions will appear here soon.',
    },
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQ Items',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
